import {useCallback, useEffect, useState} from 'react';

import DocumentStore from 'notebook/documents/DocumentStore';
import {useStatusBarSaveState} from 'notebook/store/useStatusBarSaveState';
import useWarnOnNavigate from 'ui-library/hooks/useWarnOnNavigate';

interface UseDocumentSaveState {
  isLatest: boolean;
  latestVersionId?: number;
  openVersionId?: number;
  isSaving: boolean;
  isDirty: boolean;
  lockedFromExternalChange: boolean;
  isAutoSaving: boolean;
  saveSuccess: boolean;
}

export function getDocumentSaveState(documentId: string): UseDocumentSaveState {
  const inStore = DocumentStore.inStore(documentId);
  const isUnsaved = inStore ? DocumentStore.hasUnsavedChanges(documentId) : false;
  // Editor state is only available if the document is in the store. Throws error otherwise.
  const editorState = inStore ? DocumentStore.getEditorState(documentId) : undefined;
  const versionState = inStore ? DocumentStore.getVersionState(documentId) : undefined;

  const isLatest = editorState?.isLatest ?? false;
  const latestVersionId = editorState?.metadata.latestVersionId;
  const openVersionId = editorState?.openVersionId;
  const isSaving = editorState?.saving ?? isUnsaved;
  const lockedFromExternalChange = editorState?.lockedFromExternalChange ?? false;
  const isAutoSaving = editorState?.isAutoSaving ?? false;
  const saveSuccess = editorState?.saveSuccess ?? true;
  const isDirty = versionState?.dirty ?? false;

  return {
    isLatest,
    latestVersionId,
    openVersionId,
    isSaving,
    isDirty,
    lockedFromExternalChange,
    isAutoSaving,
    saveSuccess,
  };
}

export default function useDocumentSaveState(documentId: string): UseDocumentSaveState {
  const {
    isLatest,
    latestVersionId,
    openVersionId,
    isSaving,
    isDirty,
    lockedFromExternalChange,
    isAutoSaving,
    saveSuccess,
  } = getDocumentSaveState(documentId);

  const [isLatestState, setIsLatestState] = useState(isLatest);
  const [latestVersionIdState, setLatestVersionIdState] = useState(latestVersionId);
  const [openVersionIdState, setOpenVersionIdState] = useState(openVersionId);
  const [isSavingState, setIsSavingState] = useState(isSaving);
  const [isDirtyState, setIsDirtyState] = useState(isDirty);
  const [saveSuccessState, setSaveSuccessState] = useState(saveSuccess);
  const [lockedFromExternalChangeState, setLockedFromExternalChangeState] =
    useState(lockedFromExternalChange);
  const [isAutoSavingState, setIsAutoSavingState] = useState(isAutoSaving);

  useWarnOnNavigate(Boolean(isSavingState), 'Leave without saving?');
  useStatusBarSaveState({
    saving: isSavingState,
    saveSuccess: saveSuccessState,
    lockedFromExternalChange: lockedFromExternalChangeState,
    isAutoSaving: isAutoSavingState,
  });

  const handleStoreChange = useCallback(() => {
    const {
      isLatest,
      latestVersionId,
      openVersionId,
      isSaving,
      isDirty,
      lockedFromExternalChange,
      isAutoSaving,
      saveSuccess,
    } = getDocumentSaveState(documentId);

    setIsLatestState(isLatest);
    setLatestVersionIdState(latestVersionId);
    setOpenVersionIdState(openVersionId);
    setIsSavingState(isSaving);
    setIsDirtyState(isDirty);
    setSaveSuccessState(saveSuccess);
    setLockedFromExternalChangeState(lockedFromExternalChange);
    setIsAutoSavingState(isAutoSaving);
  }, [documentId]);

  useEffect(() => {
    // Need to listen to the DocumentStore for updates to the DocumentSaveState
    DocumentStore.addChangeListener(handleStoreChange);
    return () => {
      DocumentStore.removeChangeListener(handleStoreChange);
    };
  }, [handleStoreChange]);

  return {
    isLatest: isLatestState,
    latestVersionId: latestVersionIdState,
    openVersionId: openVersionIdState,
    isSaving: isSavingState,
    isDirty: isDirtyState,
    lockedFromExternalChange: lockedFromExternalChangeState,
    isAutoSaving: isAutoSavingState,
    saveSuccess: saveSuccessState,
  };
}
